import { useEffect, useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useHasPermission } from '@/auth/auth-store';
import { useLegalEntities } from '@/hooks/use-fiscal-registration';
import {
  SIGNING_KEY_ERROR,
  SIGNING_KEY_ERROR_KEY,
  SIGNING_KEY_STATUS_LABEL,
  SIGNING_KEY_STATUS_LABEL_KEY,
  readKeyFile,
  signingKeyFailure,
  signingKeyWarningMessages,
  useFiscalSigningKeys,
  useLockSigningKey,
  useReplaceSigningKey,
  useRevokeSigningKey,
  useUnlockSigningKey,
  useUploadSigningKey,
  type SigningCertificate,
  type SigningKey,
  type SigningKeyStatus,
} from '@/hooks/use-fiscal-signing-keys';
import { useT } from '@/i18n/use-t';
import { EmptyPanel, FiscalCabinetSection } from './fiscal-cabinet-section';
import { Banner, SelectInput } from './fiscal-form-fields';

type T = (key: string, fallback: string) => string;

/** Largest files the form sends: the API's limits for a container and a certificate. */
const MAX_CONTAINER_BYTES = 32 * 1024;
const MAX_CERTIFICATE_BYTES = 8 * 1024;

const STATUS_VARIANT: Record<SigningKeyStatus, 'success' | 'destructive' | 'outline' | 'default'> =
  {
    active: 'success',
    expiring: 'default',
    expired: 'destructive',
    revoked: 'outline',
    replaced: 'outline',
  };

/** The operator's message for an API refusal, by its stable code. */
export function signingKeyErrorMessage(err: unknown, t: T): string {
  const { code, retryAfterSeconds } = signingKeyFailure(err);
  const key = code === null ? undefined : SIGNING_KEY_ERROR_KEY[code];
  const fallback = code === null ? undefined : SIGNING_KEY_ERROR[code];
  if (key !== undefined && fallback !== undefined) {
    return t(key, fallback).replace('{seconds}', String(retryAfterSeconds ?? 30));
  }
  return t('fiscalCabinet.signingKeys.error.generic', 'The request failed. Try again.');
}

function dateOnly(iso: string): string {
  return iso.slice(0, 10);
}

/** mm:ss until `expiresAt`, ticking every second; null once it has passed. */
function useRemaining(expiresAt: string | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (expiresAt === null) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);
  if (expiresAt === null) return null;
  const left = Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
  if (left === 0) return null;
  const minutes = Math.floor(left / 60);
  const seconds = left % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function taxIdOf(cert: SigningCertificate): string {
  return [cert.drfo, cert.edrpou].filter((v) => v !== null).join(' / ') || '—';
}

/** Lock / unlock with the remaining time of the signing session. */
function SessionControl({ signingKey, canSign }: { signingKey: SigningKey; canSign: boolean }) {
  const t = useT('admin_ui');
  const unlock = useUnlockSigningKey();
  const lock = useLockSigningKey();
  const [asking, setAsking] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const remaining = useRemaining(signingKey.session.expiresAt);
  const unlocked = signingKey.session.unlocked && remaining !== null;

  if (!signingKey.current || signingKey.status === 'expired') {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  async function onUnlock(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setWarnings([]);
    const typed = password;
    // The password leaves the component with this request and nowhere else.
    setPassword('');
    try {
      const session = await unlock.mutateAsync({ id: signingKey.id, password: typed });
      setWarnings(signingKeyWarningMessages(session?.warnings, t));
      setAsking(false);
    } catch (err) {
      setError(signingKeyErrorMessage(err, t));
    } finally {
      unlock.reset();
    }
  }

  async function onLock() {
    setError(null);
    setWarnings([]);
    try {
      await lock.mutateAsync(signingKey.id);
    } catch (err) {
      setError(signingKeyErrorMessage(err, t));
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        {unlocked ? (
          <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
            {t('fiscalCabinet.signingKeys.session.unlocked', 'Unlocked · {time} left').replace(
              '{time}',
              remaining ?? '',
            )}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            {t('fiscalCabinet.signingKeys.session.locked', 'Locked')}
          </span>
        )}
        {canSign && unlocked && (
          <Button variant="outline" size="sm" onClick={onLock} disabled={lock.isPending}>
            {t('fiscalCabinet.signingKeys.session.lock', 'Lock')}
          </Button>
        )}
        {canSign && !unlocked && !asking && (
          <Button variant="outline" size="sm" onClick={() => setAsking(true)}>
            {t('fiscalCabinet.signingKeys.session.unlock', 'Unlock')}
          </Button>
        )}
      </div>
      {canSign && !unlocked && asking && (
        <form className="flex flex-wrap items-center gap-2" onSubmit={onUnlock}>
          <Input
            type="password"
            autoComplete="off"
            aria-label={t('fiscalCabinet.signingKeys.field.password', 'Key password')}
            placeholder={t('fiscalCabinet.signingKeys.field.password', 'Key password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-9 w-44"
          />
          <Button type="submit" size="sm" disabled={password === '' || unlock.isPending}>
            {unlock.isPending
              ? t('fiscalCabinet.signingKeys.session.unlocking', 'Unlocking…')
              : t('fiscalCabinet.signingKeys.session.unlock', 'Unlock')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setAsking(false);
              setPassword('');
              setError(null);
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
        </form>
      )}
      {error !== null && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      {warnings.map((warning) => (
        <p key={warning} role="status" className="text-xs text-amber-700 dark:text-amber-400">
          {warning}
        </p>
      ))}
    </div>
  );
}

/** PFC-20 — an OCSP warning after an upload or replace (the key was accepted). */
function WarningNotice({ messages, onClose }: { messages: string[]; onClose: () => void }) {
  const t = useT('admin_ui');
  if (messages.length === 0) return null;
  return (
    <div
      role="status"
      className="flex items-start justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
    >
      <div className="space-y-1">
        {messages.map((message) => (
          <p key={message}>{message}</p>
        ))}
      </div>
      <Button variant="ghost" size="sm" onClick={onClose}>
        {t('common.close', 'Close')}
      </Button>
    </div>
  );
}

function CertificateDetails({ title, cert }: { title: string; cert: SigningCertificate }) {
  const t = useT('admin_ui');
  return (
    <div className="space-y-1 text-xs">
      <p className="font-medium">{title}</p>
      <p>{cert.ownerName || '—'}</p>
      <p className="font-mono">{cert.serial}</p>
      <p>
        {t('fiscalCabinet.signingKeys.detail.issuer', 'Issuer')}: {cert.issuer || '—'}
      </p>
      <p>
        {t('fiscalCabinet.signingKeys.detail.validity', 'Valid {from} – {to}')
          .replace('{from}', dateOnly(cert.validFrom))
          .replace('{to}', dateOnly(cert.validTo))}
      </p>
      <p>
        {t('fiscalCabinet.signingKeys.detail.keyUsage', 'Key usage')}:{' '}
        {cert.keyUsage.join(', ') || '—'}
      </p>
    </div>
  );
}

/** Upload a key for a legal entity, or a new key replacing `replacing`. */
function SigningKeyUploadForm({
  replacing,
  onDone,
}: {
  replacing: SigningKey | null;
  /** Back to the list, with the warnings the upload answered (if any). */
  onDone: (warnings?: string[]) => void;
}) {
  const t = useT('admin_ui');
  const entities = useLegalEntities(replacing === null);
  const upload = useUploadSigningKey();
  const replace = useReplaceSigningKey();
  const [legalEntityId, setLegalEntityId] = useState('');
  const [container, setContainer] = useState<File | null>(null);
  const [certificates, setCertificates] = useState<File[]>([]);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const pending = upload.isPending || replace.isPending;
  const active = (entities.data?.items ?? []).filter((e) => e.active);
  const entityId = replacing?.legalEntity.id ?? (legalEntityId || active[0]?.id || '');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (container === null) {
      setError(t('fiscalCabinet.signingKeys.pickFile', 'Choose the key file.'));
      return;
    }
    const tooBig =
      container.size > MAX_CONTAINER_BYTES ||
      certificates.some((f) => f.size > MAX_CERTIFICATE_BYTES);
    if (tooBig || [container, ...certificates].some((f) => f.size === 0)) {
      setError(
        t('fiscalCabinet.signingKeys.fileSize', 'A key or certificate file is empty or too large.'),
      );
      return;
    }
    const typed = password;
    // Cleared at once: the password lives only in the request below.
    setPassword('');
    try {
      const body = {
        container: await readKeyFile(container),
        certificates: await Promise.all(certificates.map(readKeyFile)),
        password: typed,
      };
      const saved =
        replacing === null
          ? await upload.mutateAsync({ ...body, legalEntityId: entityId })
          : await replace.mutateAsync({ id: replacing.id, body });
      onDone(signingKeyWarningMessages(saved?.warnings, t));
    } catch (err) {
      setError(signingKeyErrorMessage(err, t));
    } finally {
      upload.reset();
      replace.reset();
    }
  }

  const noEntity = replacing === null && entities.data !== undefined && active.length === 0;

  return (
    <form
      className="space-y-4 rounded-md border border-border bg-card p-5"
      onSubmit={onSubmit}
      aria-label={t('fiscalCabinet.signingKeys.uploadTitle', 'Upload a signing key')}
    >
      <h2 className="text-base font-medium">
        {replacing === null
          ? t('fiscalCabinet.signingKeys.uploadTitle', 'Upload a signing key')
          : t(
              'fiscalCabinet.signingKeys.replaceTitle',
              'Replace the signing key of {entity}',
            ).replace('{entity}', replacing.legalEntity.name)}
      </h2>
      {noEntity ? (
        <p className="text-sm text-muted-foreground">
          {t(
            'fiscalCabinet.signingKeys.noLegalEntity',
            'Add a legal entity first: a key always belongs to one.',
          )}
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {replacing === null && (
            <div>
              <Label htmlFor="sk-entity">
                {t('fiscalCabinet.signingKeys.field.legalEntity', 'Legal entity')}
              </Label>
              <div className="mt-1">
                <SelectInput id="sk-entity" value={entityId} onChange={setLegalEntityId}>
                  {active.map((entity) => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name} ({entity.tin})
                    </option>
                  ))}
                </SelectInput>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="sk-container">
              {t('fiscalCabinet.signingKeys.field.container', 'Key file (Key-6.dat, .pfx, .p12)')}
            </Label>
            <input
              id="sk-container"
              type="file"
              className="mt-1 block w-full text-sm"
              onChange={(e) => setContainer(e.target.files?.[0] ?? null)}
            />
          </div>
          <div>
            <Label htmlFor="sk-certificates">
              {t(
                'fiscalCabinet.signingKeys.field.certificates',
                'Certificate files (.cer), if they came separately',
              )}
            </Label>
            <input
              id="sk-certificates"
              type="file"
              multiple
              accept=".cer,.crt,.der"
              className="mt-1 block w-full text-sm"
              onChange={(e) => setCertificates(Array.from(e.target.files ?? []))}
            />
          </div>
          <div>
            <Label htmlFor="sk-password">
              {t('fiscalCabinet.signingKeys.field.password', 'Key password')}
            </Label>
            <Input
              id="sk-password"
              type="password"
              autoComplete="off"
              className="mt-1"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t(
                'fiscalCabinet.signingKeys.passwordHint',
                'Used once to read the key. It is not stored.',
              )}
            </p>
          </div>
        </div>
      )}
      {error !== null && <Banner>{error}</Banner>}
      <div className="flex gap-2">
        {!noEntity && (
          <Button type="submit" disabled={pending || password === '' || entityId === ''}>
            {pending
              ? t('fiscalCabinet.signingKeys.checking', 'Checking the key…')
              : replacing === null
                ? t('fiscalCabinet.signingKeys.submitUpload', 'Upload')
                : t('fiscalCabinet.signingKeys.submitReplace', 'Replace key')}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={() => onDone()}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </form>
  );
}

function RevokeForm({ signingKey, onDone }: { signingKey: SigningKey; onDone: () => void }) {
  const t = useT('admin_ui');
  const revoke = useRevokeSigningKey();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await revoke.mutateAsync({ id: signingKey.id, reason: reason.trim() });
      onDone();
    } catch (err) {
      setError(signingKeyErrorMessage(err, t));
    }
  }

  return (
    <form className="space-y-2" onSubmit={onSubmit}>
      <p className="text-xs text-muted-foreground">
        {t(
          'fiscalCabinet.signingKeys.revoke.warning',
          'A revoked key cannot be unlocked or used again.',
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label={t('fiscalCabinet.signingKeys.revoke.reason', 'Reason for revoking')}
          placeholder={t('fiscalCabinet.signingKeys.revoke.reason', 'Reason for revoking')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="h-9 w-64"
          maxLength={500}
        />
        <Button
          type="submit"
          variant="destructive"
          size="sm"
          disabled={reason.trim() === '' || revoke.isPending}
        >
          {t('fiscalCabinet.signingKeys.revoke.confirm', 'Revoke key')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
      {error !== null && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </form>
  );
}

function SigningKeyRow({
  signingKey,
  canSign,
  onReplace,
}: {
  signingKey: SigningKey;
  canSign: boolean;
  onReplace: (key: SigningKey) => void;
}) {
  const t = useT('admin_ui');
  const [open, setOpen] = useState<'details' | 'revoke' | null>(null);
  const k = signingKey;
  const statusLabel = t(SIGNING_KEY_STATUS_LABEL_KEY[k.status], SIGNING_KEY_STATUS_LABEL[k.status]);

  return (
    <>
      <TR>
        <TD>
          <div>{k.legalEntity.name}</div>
          <div className="font-mono text-xs text-muted-foreground">{k.legalEntity.tin}</div>
        </TD>
        <TD>{k.signing.ownerName || '—'}</TD>
        <TD className="font-mono text-xs">{taxIdOf(k.signing)}</TD>
        <TD className="font-mono text-xs">{k.signing.serial}</TD>
        <TD>
          <div>{dateOnly(k.expiresAt)}</div>
          {(k.status === 'active' || k.status === 'expiring') && (
            <div className="text-xs text-muted-foreground">
              {t('fiscalCabinet.signingKeys.daysLeft', '{days} days left').replace(
                '{days}',
                String(k.daysLeft),
              )}
            </div>
          )}
        </TD>
        <TD>
          <Badge variant={STATUS_VARIANT[k.status]}>{statusLabel}</Badge>
        </TD>
        <TD>
          <SessionControl signingKey={k} canSign={canSign} />
        </TD>
        <TD className="text-right">
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(open === 'details' ? null : 'details')}
            >
              {open === 'details'
                ? t('fiscalCabinet.signingKeys.action.hideDetails', 'Hide details')
                : t('fiscalCabinet.signingKeys.action.details', 'Details')}
            </Button>
            {canSign && k.current && (
              <>
                <Button variant="outline" size="sm" onClick={() => onReplace(k)}>
                  {t('fiscalCabinet.signingKeys.action.replace', 'Replace')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setOpen('revoke')}>
                  {t('fiscalCabinet.signingKeys.action.revoke', 'Revoke')}
                </Button>
              </>
            )}
          </div>
        </TD>
      </TR>
      {open !== null && (
        <TR>
          <TD colSpan={8} className="bg-muted/30">
            {open === 'revoke' ? (
              <RevokeForm signingKey={k} onDone={() => setOpen(null)} />
            ) : (
              <div className="grid gap-4 md:grid-cols-3">
                <CertificateDetails
                  title={t('fiscalCabinet.signingKeys.detail.signing', 'Signing certificate')}
                  cert={k.signing}
                />
                <CertificateDetails
                  title={t(
                    'fiscalCabinet.signingKeys.detail.keyAgreement',
                    'Encryption certificate',
                  )}
                  cert={k.keyAgreement}
                />
                <div className="space-y-1 text-xs">
                  <p className="font-medium">
                    {t('fiscalCabinet.signingKeys.detail.files', 'Uploaded files')}
                  </p>
                  {[k.containerFileName, ...k.certificateFileNames]
                    .filter((name): name is string => name !== null)
                    .map((name) => (
                      <p key={name} className="font-mono">
                        {name}
                      </p>
                    ))}
                  {k.revokedReason !== null && (
                    <p>
                      {t('fiscalCabinet.signingKeys.revoke.reason', 'Reason for revoking')}:{' '}
                      {k.revokedReason}
                    </p>
                  )}
                </div>
              </div>
            )}
          </TD>
        </TR>
      )}
    </>
  );
}

function SigningKeysBody() {
  const t = useT('admin_ui');
  const canSign = useHasPermission('fiscal.cabinet.sign');
  const [showAll, setShowAll] = useState(false);
  const { data, isLoading, isError } = useFiscalSigningKeys(showAll ? 'all' : 'current', true);
  // null: the list; 'new': the upload form; a key: the form replacing it.
  const [form, setForm] = useState<SigningKey | 'new' | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  if (form !== null) {
    return (
      <SigningKeyUploadForm
        replacing={form === 'new' ? null : form}
        onDone={(answered) => {
          setWarnings(answered ?? []);
          setForm(null);
        }}
      />
    );
  }
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  }
  if (isError || data === undefined) {
    return (
      <p className="text-sm text-destructive">
        {t('fiscalCabinet.signingKeys.loadError', 'Failed to load the signing keys.')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <WarningNotice messages={warnings} onClose={() => setWarnings([])} />
      <p className="text-sm text-muted-foreground">
        {t(
          'fiscalCabinet.signingKeys.intro',
          'Each legal entity signs with its own qualified key. The key password is never stored: it is asked when the key is uploaded and each time it is unlocked for a 30-minute signing session, which everyone here who may sign then shares.',
        )}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          {t('fiscalCabinet.signingKeys.showAll', 'Show revoked and replaced keys')}
        </label>
        {canSign && (
          <Button onClick={() => setForm('new')}>
            {t('fiscalCabinet.signingKeys.upload', 'Upload key')}
          </Button>
        )}
      </div>
      {data.items.length === 0 ? (
        <EmptyPanel>
          {t(
            'fiscalCabinet.signingKeys.empty',
            'No signing key has been uploaded yet, so nothing can be signed.',
          )}
        </EmptyPanel>
      ) : (
        <div className="rounded-md border border-border bg-card">
          <Table>
            <THead>
              <TR>
                <TH>{t('fiscalCabinet.signingKeys.col.legalEntity', 'Legal entity')}</TH>
                <TH>{t('fiscalCabinet.signingKeys.col.owner', 'Key holder')}</TH>
                <TH>{t('fiscalCabinet.signingKeys.col.taxId', 'Tax ID (RNOKPP / EDRPOU)')}</TH>
                <TH>{t('fiscalCabinet.signingKeys.col.serial', 'Certificate serial')}</TH>
                <TH>{t('fiscalCabinet.signingKeys.col.validUntil', 'Valid until')}</TH>
                <TH>{t('fiscalCabinet.signingKeys.col.status', 'Status')}</TH>
                <TH>{t('fiscalCabinet.signingKeys.col.session', 'Signing session')}</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {data.items.map((key) => (
                <SigningKeyRow
                  key={key.id}
                  signingKey={key}
                  canSign={canSign}
                  onReplace={setForm}
                />
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/** PFC-13 — Fiscal cabinet → Signing keys: upload, status, signing session, revoke, replace. */
export function FiscalSigningKeysPage() {
  return <FiscalCabinetSection>{() => <SigningKeysBody />}</FiscalCabinetSection>;
}
